import { IKHInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
export { IKHInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import type { RegistrationStammdatenPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { validate } from '@adornis/validation/decorators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';

export const IKH_UNIVERSITY_ADDRESSES: readonly string[] = [
  'Theodor-W.-Adorno-Platz',
  'Max-von-Laue-Strasse',
  'Grüneburgplatz',
  'Ginnheimer Landstraße 39',
  'Senckenberganlage 31',
  'Theodor-Stern-Kai 7',
  'Norbert-Wollheim-Platz',
];

export const normalizeStreetForComparison = (value: Maybe<string>): string => {
  return (value ?? '')
    .toLowerCase()
    .trim()
    .replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/strasse\b|str\.|str\b/g, 'str')
    .replace(/[-.]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/(\d)\s+([a-z])\b/g, '$1$2')
    .trim();
};

export const ikhRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::ikh',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new IKHStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  customValidate: (stammdaten: IKHStammdaten): string | undefined => {
    const ad = stammdaten.address;
    const zipCity = ad?.zipCity;
    const isFrankfurt =
      !!zipCity && (zipCity.city?.toLowerCase().includes('frankfurt') || /^6[05]/.test(zipCity.zip ?? ''));
    if (stammdaten.invoiceReceiver === IKHInvoiceReceiverEnum.IKHPRIVATE && isFrankfurt) {
      const street = normalizeStreetForComparison(ad?.streetAndHousenumber);
      if (IKH_UNIVERSITY_ADDRESSES.some(place => street.startsWith(normalizeStreetForComparison(place))))
        return 'Private Rechnungen können nicht an die Universität Frankfurt gestellt werden';
    }
    return undefined;
  },
  getFields: () => {
    return Object.keys(IKHStammdaten.fields).filter(it => !['_id', '_class', 'userID', 'institutionID'].includes(it));
  },
  classType: () => IKHStammdaten,
};

export enum IKHSalutationEnum {
  IKHFRAU = 'IKHFRAU',
  IKHHERR = 'IKHHERR',
  IKHSONSTIGES = 'IKHSONSTIGES',
}
registerEnum({ enum: IKHSalutationEnum, name: 'IKHSalutationEnum' });

export enum IKHOccupationEnum {
  IKHPROF_GOETHE = 'IKHPROF_GOETHE',
  IKHSINCE_GOETHE = 'IKHSINCE_GOETHE',
  IKHTEACHER_GOETHE = 'IKHTEACHER_GOETHE',
  IKHHIWI_GOETHE = 'IKHHIWI_GOETHE',
  IKHEMPLOYEE_GOETHE = 'IKHEMPLOYEE_GOETHE',
  IKHTEACHER = 'IKHTEACHER',
  IKHHESS_HOCHSCHULANGEHOERIGE = 'IKHHESS_HOCHSCHULANGEHOERIGE',
  IKHSONSTIGE = 'IKHSONSTIGE',
}
registerEnum({ enum: IKHOccupationEnum, name: 'IKHOccupationEnum' });

export enum IKHInstitutionEnum {
  IKHRECHT = 'IKHRECHT',
  IKHWIRTSCHAFT = 'IKHWIRTSCHAFT',
  IKHGESELLSCHAFT = 'IKHGESELLSCHAFT',
  IKHERZIEHUNG = 'IKHERZIEHUNG',
  IKHPSYCHOLOGIE = 'IKHPSYCHOLOGIE',
  IKHEVANG_THEOLOGIE = 'IKHEVANG_THEOLOGIE',
  IKHKATH_THEOLOGIE = 'IKHKATH_THEOLOGIE',
  IKHPHILOSOPHIE = 'IKHPHILOSOPHIE',
  IKHSPRACHEN = 'IKHSPRACHEN',
  IKHNEU_PHILOLOGIE = 'IKHNEU_PHILOLOGIE',
  IKHGEO = 'IKHGEO',
  IKHINFORMATIK = 'IKHINFORMATIK',
  IKHPHYSIK = 'IKHPHYSIK',
  IKHCHEMIE = 'IKHCHEMIE',
  IKHBIOLOGIE = 'IKHBIOLOGIE',
  IKHMEDIZIN = 'IKHMEDIZIN',
  IKHEXTERN = 'IKHEXTERN',
  IKHZENTRALE = 'IKHZENTRALE',
  IKHOTHERS = 'IKHOTHERS',
}
registerEnum({ enum: IKHInstitutionEnum, name: 'IKHInstitutionEnum' });
registerEnum({ enum: IKHInvoiceReceiverEnum, name: 'IKHInvoiceReceiverEnum' });

export const isIkhInternalInvoiceReceiverBlocked = (
  stammdaten: Pick<IKHStammdaten, 'occupation' | 'department'>,
): boolean =>
  stammdaten?.occupation === IKHOccupationEnum.IKHTEACHER_GOETHE ||
  stammdaten?.occupation === IKHOccupationEnum.IKHTEACHER ||
  stammdaten?.occupation === IKHOccupationEnum.IKHHESS_HOCHSCHULANGEHOERIGE ||
  stammdaten?.department === IKHInstitutionEnum.IKHEXTERN;

@Entity()
export class IKHStammdaten extends UserStammdaten {
  static override _class = 'IKHStammdaten';

  override getTranslationNamespace() {
    return 'ikh';
  }

  @Field(type => IKHSalutationEnum)
  salutation: Maybe<IKHSalutationEnum>;

  @Field(type => String)
  title: Maybe<string>;

  @validate(and(nonOptional()))
  @Field(type => String)
  phoneNumber: Maybe<string>;

  @Field(type => IKHInstitutionEnum)
  department: Maybe<IKHInstitutionEnum>;

  /**
   * only applicable for external department
   */
  @Field(type => String)
  externalDepartmentUniversity: Maybe<string>;

  /**
   * only applicable for external department
   */
  @Field(type => String)
  externalDepartmentDepartment: Maybe<string>;

  /**
   * only applicable for others department
   */
  @Field(type => String)
  otherDepartmentFurtherInformation: Maybe<string>;

  @validate(nonOptional())
  @Field(type => IKHOccupationEnum)
  occupation: Maybe<IKHOccupationEnum>;

  @Field(type => String)
  hessHochschulDepartment: Maybe<string>;

  @validate(
    and(nonOptional(), ({ value, target, key }) => {
      if (value === IKHInvoiceReceiverEnum.IKHINTERNAL && isIkhInternalInvoiceReceiverBlocked(target as IKHStammdaten))
        throw new ValidationError('Invalid invoice receiver', {
          key,
          target,
          translationKey: 'invalid_invoice_receiver',
        });
    }),
  )
  @Field(type => IKHInvoiceReceiverEnum, {
    dynamicValidator: 'non_optional',
    default: v => (v ?? IKHInvoiceReceiverEnum.IKHPRIVATE) as typeof IKHInvoiceReceiverEnum,
  })
  invoiceReceiver: Maybe<IKHInvoiceReceiverEnum>;

  /**
   * only applicable for external invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL) return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  institution: Maybe<string>;

  /**
   * only applicable for external invoice receiver
   */
  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL) return nonOptional()({ value, target, key });
  })
  @Field(type => String)
  institutionEmail: Maybe<string>;

  /**
   * only applicable for internal invoice receiver
   */
  @Field(type => String)
  division: Maybe<string>;

  /**
   * only applicable for internal invoice receiver
   */
  @Field(type => String)
  postbox: Maybe<string>;

  /**
   * - only applicable for internal invoice receiver
   * - non optional
   */
  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver !== IKHInvoiceReceiverEnum.IKHINTERNAL) return;
    nonOptional()({ value, target, key });
    if (!/^\d+$/.test(value))
      throw new ValidationError(`Key ${key} must contain digits only!`, {
        key,
        translationKey: 'validation_digits_only',
        target,
      });
  })
  @Field(type => String)
  costCenter: Maybe<string>;

  @Field(type => String)
  attention: Maybe<string>;

  @validate(({ value, target, key }) => {
    if (target.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL) return;
    const errorFields = (
      !target.address
        ? [{ path: ['address'], target }]
        : [
            !target.address?.streetAndHousenumber
              ? { path: ['address', 'streetAndHousenumber'], target: target.address }
              : null,
            ...(!target.address.zipCity
              ? [{ path: ['address', 'zipCity'], target: target.address }]
              : [
                  !target.address?.zipCity?.zip
                    ? { path: ['address', 'zipCity', 'zip'], target: target.address?.zipCity }
                    : null,
                  !target.address?.zipCity?.city
                    ? { path: ['address', 'zipCity', 'city'], target: target.address?.zipCity }
                    : null,
                ]),
          ]
    ).filter(Boolean) as Array<{ path: string[]; target: any }>;

    if (errorFields.length)
      throw new MultipleValidationError(
        errorFields.map(
          ({ path, target }) =>
            new ValidationError(`Key ${path.slice(-1)[0]} is required!`, {
              key: path.slice(-1)[0] as any,
              translationKey: 'validation_non_optional',
              path: path.slice(0, -1),
              target,
            }),
        ),
      );
  })
  @Field(type => Address, { default: v => v ?? new Address({}) })
  address: Maybe<Address>;
}
